import React, {
  memo, useCallback, useId, useMemo, useState,
} from 'react'

import {
  FiltersSidebar, Button, Icon, Search,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, object, shape, string,
} from 'prop-types'

import { getFeatureFilter } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { FEATURES, DEFAULT_LIMIT } from './constants'

function FeaturesContent({ features = FEATURES, searchInputRef = null, hideReset = false }) {
  const translate = useTranslate()
  const reactId = useId()
  // Colons are legal in an id but need escaping in selectors; strip them as CollapsibleSection does.
  const searchInputId = `features-search-${reactId.replace(/:/g, '')}`

  // Selecting a joined string keeps zustand's strict-equality happy while the list of
  // subscribed slices lives in each feature's `select`, not hardcoded per asset type.
  const selectedKey = useSearchPageStore(useCallback(
    state => features.filter(feature => feature.select(state)).map(feature => feature.value).join(','),
    [features],
  ))
  const selectedFeatures = useMemo(() => (selectedKey ? selectedKey.split(',') : []), [selectedKey])
  // Closing the dropdown unmounts this, so a selection past DEFAULT_LIMIT would come back hidden
  // behind "Show more" with nothing saying it is on. Start expanded when one is, and only on
  // mount - re-deriving would refuse to collapse while such a filter is applied.
  const [showAll, setShowAll] = useState(() => features
    .slice(DEFAULT_LIMIT)
    .some(feature => selectedFeatures.includes(feature.value)))
  const [searchTerm, setSearchTerm] = useState('')
  const trackFilterSelect = useTrackFilterSelect()

  const translatedFeatures = useMemo(() => features.map(({ value, labelKey }) => ({
    value,
    label: translate(`search_page.filters.${labelKey}`),
  })), [features, translate])

  // Searching is handled here instead of FiltersGroup's internal state so the empty
  // result can render a "No matches" message, which FiltersGroup has no slot for.
  const visibleFeatures = useMemo(() => {
    if (!searchTerm) return translatedFeatures
    const normalized = searchTerm.toLowerCase()
    return translatedFeatures.filter(({ label }) => label.toLowerCase().includes(normalized))
  }, [translatedFeatures, searchTerm])

  // Single group, no sub-heading: the collapsible header already names the section "Features".
  const featuresConfig = useMemo(() => [{
    label: '',
    isSearchable: true,
    filters: visibleFeatures,
  }], [visibleFeatures])

  const handleChange = useCallback((_, value, isOn) => {
    const feature = features.find(item => item.value === value)
    if (feature && isOn) {
      const { name, value: filterValue } = getFeatureFilter(feature.value)
      trackFilterSelect(name, filterValue)
    }
    feature?.onChange(isOn)
  }, [features, trackFilterSelect])

  // Delegating to each feature's own onChange keeps the low-poly nuance in the constants:
  // its off-toggle removes only its polygon range, so other Poly count selections survive.
  const handleReset = useCallback(() => {
    features.forEach(feature => feature.onChange(false))
  }, [features])

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value)
  }, [])

  const searchLabel = translate('search_page.filters.features.search_placeholder')

  const textInputProps = useMemo(() => ({
    // FiltersGroup derives the input id from its group label, which is empty here, so every
    // instance would answer to id="search-for-" - the secondary bar and the More filters drawer
    // can be mounted at the same time.
    id: searchInputId,
    // The group label is empty, so the placeholder is the only name the input has - and it stops
    // being exposed once the user types.
    'aria-label': searchLabel,
    innerRef: searchInputRef,
    value: searchTerm,
    onChange: handleSearchChange,
    leadIcon: <Icon notSpaced svgComponent={Search} color="dark" />,
    textInputClassName: 'filter-wrapper__search-input',
  }), [searchInputId, searchLabel, searchInputRef, searchTerm, handleSearchChange])

  const isSearching = searchTerm.length > 0
  // limit=0 tells FiltersGroup to show all items; a search always shows every match,
  // otherwise toggling reveals the rest behind "Show more".
  const effectiveLimit = (showAll || isSearching) ? 0 : DEFAULT_LIMIT
  const hasMore = !isSearching && features.length > DEFAULT_LIMIT
  const hasSelectedAnyFilters = selectedFeatures.length > 0

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={featuresConfig}
        selectedFilters={selectedFeatures}
        onFiltersChange={handleChange}
        limit={effectiveLimit}
        isControlled
        useNewCheckboxes
        hideShowMore
        hideLastDivider
        searchPlaceholder={searchLabel}
        textInputProps={textInputProps}
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {/* Always mounted: focus stays in the search field, so the message has to be announced by a
          live region that already exists rather than inserted alongside the text it carries. */}
      <span className="filter-wrapper__empty cgt-body-base" role="status" aria-live="polite">
        {visibleFeatures.length === 0 ? translate('search_page.filters.features.no_matches') : ''}
      </span>
      {hasMore && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(value => !value)}
        >
          {translate(`search_page.filters.features.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button"
          onClick={handleReset}
          disabled={!hasSelectedAnyFilters}
        >
          {translate('search_page.filters.features.reset')}
        </Button>
      )}
    </>
  )
}

FeaturesContent.propTypes = {
  features: arrayOf(shape({
    value: string.isRequired,
    labelKey: string.isRequired,
    select: func.isRequired,
    onChange: func.isRequired,
  })),
  searchInputRef: object,
  hideReset: bool,
}

export default memo(FeaturesContent)
